/**
 * The quote sheet — paste lines, price them, adjust, save a draft (plan 0.8, Phase 2).
 *
 * ⚖️ The quote lives in isg-pricing: this sheet asks pricing's engine through
 * /api/sales/quotes and saves into the SAME quote list the pricing desk works from.
 * Pricing decides which Company's catalogue prices it — the one that sells this
 * account's division — and a part that catalogue does not carry comes back
 * unpriced. The sheet says so; it never borrows another Company's price.
 *
 * Every price shows its "why" (they last paid / last quoted / similar customers /
 * cheapest competitor / cost plus) — a suggestion a rep cannot explain is one they
 * will not use. Delivery is typed per line: the PDF prints exactly what is typed,
 * and nothing when nothing is (it used to promise "Ex Stock" on every line).
 *
 * Saves dispatch `sales:quotes-changed` ({entity, code}); linking to an opportunity
 * also dispatches `sales:items-changed`.
 *
 * **Step 0 — what the customer actually sent** (2026-09-21). A rep drops the email, PO,
 * spreadsheet, scan or phone photo in and pricing reads it (`/quotes/parse`), resolving
 * every line against the account's own Company — including the customer's OWN part
 * numbers, which is why the account is always sent with it. 🔴 **Nothing is priced
 * unattended except `matched` lines**: a `suggested` or `ambiguous` line makes the rep
 * pick, and an `unrecognised` one shows what vault's register knows about that number.
 * ⚠️ A repeated part is a PRICE-BREAK request, never a line to add up.
 */
import { escapeHtml as esc, getIcon, showError, showModal, showSuccess, store } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { canWrite } from '../services/sales-role.js';
import { money, shortDate } from '../lib/fig.js';
import { saving } from './log-sheet.js';
import { bookOverrideNote, bookPill, confidenceBadge, loadCompetitors, marginCell, quoteContext, workingsHtml } from './quote-workings.js';
import { loadPriceBooks } from './account-price-books.js';

const BASIS = {
  own_last: 'they last paid', quoted_last: 'last quoted to them', prev_quote: 'their previous quote',
  cohort: 'similar customers pay', vendor_landscape: 'competitor prices', cost_plus: 'cost plus margin',
  price_book: 'their price book',
};
const FLAG = {
  no_data: 'No price data — set a price', margin_max_capped: 'capped at the maximum margin',
  margin_floor_raised: 'raised to the margin floor', above_market: 'above the market', winback_capped: 'win-back cap',
  price_book_overridden_by_winback: 'win-back, not their price book', price_book_suspect_unit: 'price book not used (unit looks wrong)',
  price_book_below_cost: 'agreed price below cost', price_book_below_margin_floor: 'agreed price under the margin floor',
  below_book_first_break: "below the book's first break",
};

/** "RNF-100 500", "RNF-100, 1,000", "RNF-100<TAB>500" or just "RNF-100" (qty 1).
 *  The quantity is the LAST number on the line; thousands separators are allowed. */
export function parseLines(text) {
  const out = [];
  const bad = [];
  (text || '').split(/\r?\n/).forEach((raw, n) => {
    const line = raw.trim();
    if (!line) return;
    const m = line.match(/^(.+?)[\s,;\t]+(\d[\d,]*(?:\.\d+)?)\s*$/);
    const sku = (m ? m[1] : line).trim().replace(/[,;]+$/, '');
    const qty = m ? Number(m[2].replace(/,/g, '')) : 1;
    if (!sku) return;
    if (!(qty > 0)) { bad.push(n + 1); return; }
    out.push({ sku, qty });
  });
  return { lines: out, bad };
}

function why(line, ccy) {
  const r = line.rationale || {};
  const s = r.signals || {};
  const m = (v) => (v == null ? '' : money(ccy, 2)(v));
  const flags = (r.flags || []).filter(f => f !== 'no_data').map(f => FLAG[f] || f.replace(/_/g, ' '));
  let text = '';
  switch (r.basis) {
    case 'own_last': text = `They last paid ${m(s.own_last)}${s.own_last_date ? ` on ${shortDate(s.own_last_date)}` : ''}${s.own_n ? ` · ${s.own_n} buys` : ''}`; break;
    case 'quoted_last': text = `Last quoted ${m(s.quoted_last)}${s.quoted_last_date ? ` on ${shortDate(s.quoted_last_date)}` : ''}`; break;
    case 'prev_quote': text = `Their previous quote: ${m(s.prev_quote)}`; break;
    case 'cohort': text = `Similar customers pay ${m(s.cohort_median)}${s.cohort_n ? ` (median of ${s.cohort_n})` : ''}`; break;
    case 'vendor_landscape': text = `Cheapest competitor ${m(s.vendor_cheapest)}`; break;
    case 'cost_plus': text = 'Cost plus the strategy’s margin'; break;
    case 'price_book': text = `Their price book ${r.price_book ? `${r.price_book.book_name}: ${money(ccy, 4)(r.price_book.price)} at ${Number(r.price_book.break_qty).toLocaleString('en-GB')}+` : ''}`; break;
    default: text = (r.flags || []).includes('no_data') ? FLAG.no_data : (BASIS[r.basis] || '');
  }
  if (r.surge && r.surge.applied) flags.push(`+${r.surge.surge_pct}% — competitors out of stock`);
  return `${esc(text)}${flags.length ? `<div class="sc-list-meta">${esc(flags.join(' · '))}</div>` : ''}`;
}

/**
 * @param {{entity, code, name}} account
 * @param {{workItem?: {id, title}, lines?: [{sku, qty}]}} opts
 */
export function openQuoteSheet(account, opts = {}) {
  const writer = canWrite(store.get('salesRole'));
  const st = { built: null, edits: {}, delivery: {}, surge: false, saved: null, busy: false,
               parse: null, pick: {}, drop: {}, qty: {}, poll: null,
               strategy: '', strategies: null, strategyNote: '',
               winback: opts.winback || null };   // {quote_no, …} when winning one back
  const content = document.createElement('div');
  const modal = showModal({
    title: `New quote — ${esc(account.name || account.code)}`, content, size: 'xl',
    actions: [{ label: 'Close', className: 'btn btn-secondary' }],
  });
  const footer = modal.element.querySelector('.modal-footer');

  function setActions(buttons) {
    footer.innerHTML = '';
    buttons.forEach(({ label, cls, fn }) => {
      const b = document.createElement('button');
      b.className = cls; b.textContent = label;
      b.addEventListener('click', fn);
      footer.appendChild(b);
    });
  }

  /** The Company's strategy library. A Company with none, or a pricing outage, leaves
   *  the picker out entirely rather than showing an empty select nobody can get past —
   *  the default strategy still prices the quote. */
  async function loadStrategies() {
    try {
      const res = await salesApi.getQuoteStrategies(
        account.entity, account.code, st.winback ? 'winback' : 'standard');
      const items = (res.data || {}).items;
      st.strategies = Array.isArray(items) && items.length ? items : null;
      if (st.strategies && !st.strategy) {
        // pricing marks its own default per context — never guess it from the name
        const marked = st.strategies.find(x => x.is_default);
        st.strategy = (res.data || {}).default || (marked && marked.key) || st.strategies[0].key;
      }
      st.strategyNote = st.strategies ? '' : (((res.meta || {}).sources || {}).pricing || {}).reason || '';
      try { st.books = (await loadPriceBooks(account)).items || []; } catch { st.books = []; }
    } catch (err) {
      st.strategies = null;
      st.strategyNote = err.message || '';
    }
    const host = content.querySelector('[data-strategy]');
    if (host) host.innerHTML = strategyControl();
    bindStrategy();
  }

  function strategyControl() {
    if (!st.strategies) {
      return st.strategyNote
        ? `<div class="sc-list-meta">Priced on the default strategy — the strategy list is
             unavailable (${esc(st.strategyNote)}).</div>` : '';
    }
    return `<label class="form-label" for="qs-strategy">How to price it${
      st.winback ? ' <span class="sc-list-meta">— win-back strategies price off the lapsed quote</span>' : ''}</label>
      <select class="form-select" id="qs-strategy">
        ${st.strategies.map(x => `<option value="${esc(x.key)}"${x.key === st.strategy ? ' selected' : ''}>${
      esc(x.name || x.key)}${x.description ? ` — ${esc(x.description)}` : ''}</option>`).join('')}
      </select>${bookHint()}`;
  }

  /** Before pricing: say up front that their own book wins over a standard strategy. */
  function bookHint() {
    const own = (st.books || []).filter(b => b.scope === 'customer' && b.priced && b.in_date !== false);
    if (!own.length) return '';
    return `<div class="sc-list-meta" style="margin-top:6px;padding:6px 8px;border-left:3px solid var(--green-600,#059669);background:var(--green-50,#ecfdf5)">
      ${getIcon('book-open', 12)} ${esc(account.name || account.code)} has its own price book (${esc(own.map(b => b.name).join(', '))}).
      Parts in it are quoted at the agreed price${st.winback ? ' — <strong>except with a win-back strategy</strong>, which prices them itself'
        : ' whichever strategy you pick; the strategy prices everything else (a win-back strategy is the exception)'}.</div>`;
  }

  function bindStrategy() {
    const sel = content.querySelector('#qs-strategy');
    if (sel) sel.addEventListener('change', () => { st.strategy = sel.value; });
  }

  // ── 1. the lines ──────────────────────────────────────────────────────────
  // a line with no quantity stays a bare part number — never "PART undefined"
  function stepLines(text = opts.text || (opts.lines || []).map(l => (l.qty ? `${l.sku} ${l.qty}` : `${l.sku}`)).join('\n')) {
    content.innerHTML = `
      ${opts.workItem ? `<p class="sc-sheet-hint">${getIcon('target', 12)} Will be linked to <strong>${esc(opts.workItem.title)}</strong>.</p>` : ''}
      ${st.winback ? `<div class="card card-body" style="border-left:3px solid var(--primary-color,#069);margin-bottom:var(--space-3)">
        ${getIcon('refresh-cw', 14)} <strong>Winning back ${esc(st.winback.quote_no)}</strong> —
        ${esc(st.winback.status_label || 'lapsed')} ${st.winback.days_since != null ? `${st.winback.days_since} days ago` : ''},
        ${esc(money(st.winback.currency || 'GBP', 2)(st.winback.value || 0))}.
        <div class="sc-list-meta">The lines and quantities are theirs. Pricing knows what they were
          offered last time, so a win-back price is capped at it rather than drifting up.
          ${st.winback.part_ordered ? ' ⚠️ They part-ordered this one — check what is left.' : ''}</div></div>` : ''}
      <div class="form-group">
        <label class="form-label" for="qs-lines">Parts and quantities</label>
        <textarea class="form-textarea" id="qs-lines" rows="8" spellcheck="false"
          placeholder="One line per part — the part number, then the quantity:&#10;RNF-100-3/64-X-STK 500&#10;DR-25-1/4-0-SP 1000"
          style="font-family:var(--font-mono,monospace)">${esc(text)}</textarea>
        <div class="sc-list-meta" data-parsed></div>
      </div>
      <div class="form-group" data-strategy>${strategyControl()}</div>
      <label style="display:flex;gap:var(--space-2);align-items:center">
        <input type="checkbox" id="qs-surge"${st.surge ? ' checked' : ''}> Add the surge uplift where competitors are out of stock</label>
      <div class="card card-body" data-drop style="margin-top:var(--space-3);border:1px dashed var(--border-color,#d4d4d8);text-align:center">
        <div>${getIcon('upload', 16)} <strong>Or send us what they sent you</strong></div>
        <p class="sc-list-meta" style="margin:var(--space-1) 0 var(--space-2)">Their email, a PO, a spreadsheet, a PDF,
          a screenshot or a photo of a fax. Drop it here, or
          <button type="button" class="btn btn-secondary btn-sm" data-pick-file>choose a file</button>
          — or paste the email above and press <em>Read it properly</em>.</p>
        <input type="file" data-file hidden
          accept=".pdf,.msg,.eml,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.heic,image/*">
        <div class="sc-list-meta" data-drop-note></div>
      </div>
      <p class="sc-sheet-hint" style="margin-top:var(--space-3)">Prices come from isg-pricing — the same engine and the same
        quote list as the pricing desk. A customer's own part numbers are recognised.</p>`;
    const ta = content.querySelector('#qs-lines');
    const note = content.querySelector('[data-parsed]');
    const show = () => {
      const { lines, bad } = parseLines(ta.value);
      note.textContent = `${lines.length} line${lines.length === 1 ? '' : 's'}${bad.length ? ` · line ${bad.join(', ')} has no usable quantity` : ''}`;
    };
    ta.addEventListener('input', show);
    show();
    bindStrategy();
    if (st.strategies === null && !st.strategyNote) loadStrategies();
    setTimeout(() => ta.focus(), 60);
    const zone = content.querySelector('[data-drop]');
    const file = content.querySelector('[data-file]');
    content.querySelector('[data-pick-file]').addEventListener('click', () => file.click());
    file.addEventListener('change', () => { if (file.files[0]) read({ file: file.files[0] }); });
    ['dragenter', 'dragover'].forEach(e => zone.addEventListener(e, (ev) => {
      ev.preventDefault(); zone.style.borderColor = 'var(--primary-color,#069)';
    }));
    ['dragleave', 'drop'].forEach(e => zone.addEventListener(e, (ev) => {
      ev.preventDefault(); zone.style.borderColor = '';
    }));
    zone.addEventListener('drop', (ev) => {
      const f = ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0];
      if (f) read({ file: f });
    });

    setActions([
      { label: 'Close', cls: 'btn btn-secondary', fn: () => modal.close() },
      { label: 'Read it properly', cls: 'btn btn-secondary',
        fn: () => read({ raw: content.querySelector('#qs-lines').value }) },
      { label: 'Price it', cls: 'btn btn-primary', fn: () => price() },
    ]);
  }

  // ── 1b. reading the customer's document ───────────────────────────────────
  const STATUS = {
    matched: ['ours', 'green'], suggested: ['did you mean', 'amber'],
    ambiguous: ['pick one', 'amber'], unrecognised: ['not ours', 'red'],
  };
  const VIA = {
    exact: '', normalised: 'spelling tidied', customer_xref: 'their part number',
    base_form: 'base part', customer_prefix: 'their prefix removed',
  };
  //: flags that belong on the QUANTITY, not in the status column — see qtyCell()
  const QTY_FLAGS = ['qty_missing', 'qty_conflict'];
  //: shown better elsewhere in the row than as a flag word beside the status
  //: the number is real but not in a unit we price in ("2 spools") — pricing, 2026-10-08
  const UNIT_FLAGS = ['uom_differs', 'uom_container', 'uom_unknown'];
  const SAID_ELSEWHERE = ['agreed', 'repeated_part', ...UNIT_FLAGS, ...QTY_FLAGS];

  /** Did a PERSON establish this quantity, or did something assume it?
   *  🔴 `qty_assumed: true` (pricing, 21 Sep) is the plain statement of it — `qty: 1`
   *  with a flag beside it reads as a real quantity to anyone consuming it literally,
   *  which is how the first cut of this screen got it wrong. Checked three ways because
   *  the cheap answer here is the expensive one on an invoice. */
  function qtyUnread(line) {
    return line.qty_assumed === true || line.qty == null
      || (line.flags || []).some(f => QTY_FLAGS.includes(f));
  }

  /** 🔴 A quantity nobody read is the most expensive thing on this screen: quoting 1
   *  where the page said 500 is a wrong price that looks perfectly ordinary. OCR loses
   *  one occasionally whatever pricing does about it (it lost a whole scanned order to
   *  a tiling bug on 21 Sep), so an assumed quantity is marked on the box itself, and
   *  a line carrying one cannot be priced until somebody types it or ticks it off. */
  /** Must a person set this quantity before it is priced? An unread one, or one
   *  counted in a unit we cannot price ("2 spools" of a per-metre cable). */
  function needsQty(line) {
    return qtyUnread(line) || (line.flags || []).some(f => f === 'uom_container' || f === 'uom_unknown');
  }

  function qtyCell(line, i) {
    const flags = line.flags || [];
    const conflict = flags.includes('qty_conflict');
    const missing = !conflict && qtyUnread(line);
    const value = st.qty[i] != null ? st.qty[i] : (line.qty == null ? '' : line.qty);
    const touched = st.qty[i] != null;
    const warn = (missing || conflict || (line.flags || []).some(f => f === 'uom_container' || f === 'uom_unknown')) && !touched;
    // ⚠️ `uom_sell` is the unit WE price in — "1000" of a cable they think of in reels
    // is 1000 METRES on our quote. Saying so costs a word and catches a real mistake.
    // `uom_differs` is pricing saying the two units actually disagree: that is not a
    // note, it is a factor-of-something error waiting to go out on a quote.
    const differs = (line.flags || []).some(f => UNIT_FLAGS.includes(f));
    // "2 spools" of cable is not 2 metres: like an unread quantity, it must be set
    // by a person before the line is priced.
    const container = !touched && (line.flags || []).some(f => f === 'uom_container' || f === 'uom_unknown');
    // ⚠️ UOM codes carry case variants in the item master (pc/PC/Pc, km/KM): compare
    // folded, or "m" against "M" reads as two different units and says so on screen.
    const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
    const unit = line.uom_sell && !same(line.uom_sell, line.uom) ? line.uom_sell : (line.uom || '');
    return `<input class="form-input" data-qty="${i}" type="number" min="0" step="0.001"
        value="${value}" style="width:92px;text-align:right;padding:4px 6px${
  warn ? ';border-color:var(--amber-500,#f59e0b);background:var(--amber-50,#fffbeb)' : ''}"
        aria-label="Quantity">
      ${differs ? `<div class="sc-list-meta" style="color:var(--amber-600,#d97706)">
        ${line.uom_note ? esc(line.uom_note) : `they wrote ${esc(line.uom || 'another unit')} — we price per ${esc(line.uom_sell || unit)}`}${container ? ' — set the quantity' : ''}</div>`
    : unit ? `<div class="sc-list-meta">priced per ${esc(unit)}</div>` : ''}
      ${warn && missing ? `<div class="sc-list-meta" style="color:var(--amber-600,#d97706)">
        no quantity on the page — check it</div>` : ''}
      ${warn && conflict ? `<div class="sc-list-meta" style="color:var(--amber-600,#d97706)">read two ways:
        ${(line.qty_alternatives || []).map(q =>
    `<button type="button" class="sc-qty-alt" data-qty-alt="${i}" data-qty-value="${esc(String(q))}"
             style="background:none;border:0;padding:0 2px;text-decoration:underline;cursor:pointer;color:inherit">${esc(String(q))}</button>`
  ).join(' or ') || 'check the page'}</div>` : ''}`;
  }

  function reading(what) {
    content.innerHTML = `<div class="card card-body" style="text-align:center;padding:var(--space-6)">
      <div class="spinner"></div>
      <p style="margin-top:var(--space-3)"><strong>${esc(what)}</strong></p>
      <p class="sc-list-meta" data-progress>A scanned order is read a page at a time, so this can take a minute.</p></div>`;
    setActions([{ label: 'Stop', cls: 'btn btn-secondary', fn: () => { clearPoll(); stepLines(st.text); } }]);
  }

  function clearPoll() { if (st.poll) { clearTimeout(st.poll); st.poll = null; } }

  async function read({ file, raw }) {
    if (st.busy) return;
    if (!file && !(raw || '').trim()) { showError('Paste what they sent, or attach the file.'); return; }
    st.busy = true;
    st.text = content.querySelector('#qs-lines') ? content.querySelector('#qs-lines').value : st.text;
    reading(file ? `Reading ${file.name}` : 'Reading what you pasted');
    try {
      const res = await salesApi.parseQuoteDocument({
        entity: account.entity, customer_code: account.code, file, raw });
      await settle(res);
    } catch (err) {
      showError(err.message || 'The document could not be read.');
      stepLines(st.text);
    } finally {
      st.busy = false;
    }
  }

  /** A job that is still running reports its progress and is polled; a finished one
   *  goes to the review step. A failure says so and gives the rep the textarea back. */
  async function settle(res, started = Date.now()) {
    const parse = (res.data || {}).parse || {};
    if (parse.status === 'failed') {
      showError(parse.error || 'The document could not be read — paste the lines instead.');
      stepLines(st.text);
      return;
    }
    if (parse.status === 'running' && parse.job_id) {
      const p = parse.progress || {};
      const note = content.querySelector('[data-progress]');
      if (note && p.step) note.textContent = `${p.step}${p.total ? ` — ${p.done || 0} of ${p.total}` : ''}…`;
      if (Date.now() - started > 180000) {
        showError('That is taking longer than it should. Paste the lines, or try again.');
        stepLines(st.text);
        return;
      }
      clearPoll();
      st.poll = setTimeout(async () => {
        try {
          settle(await salesApi.pollQuoteParse(parse.job_id, account.entity), started);
        } catch (err) {
          showError(err.message || 'Lost track of that upload — send it again.');
          stepLines(st.text);
        }
      }, 2000);
      return;
    }
    st.parse = parse;
    st.pick = {}; st.drop = {}; st.qty = {}; st.found = {}; st.onlyAttention = false;
    stepRead();
  }

  /** Does a person need to look at this line before it is quoted? Pricing has not
   *  settled the part, or it is a low-confidence match, or its quantity is not read. */
  function needsYou(line, i) {
    if (st.drop[i]) return false;
    if (!chosen(line, i)) return true;
    if (st.pick[i]) return false;                       // the rep has chosen
    return line.confidence === 'low' || (st.qty[i] == null && needsQty(line));
  }

  /** The part this line will be quoted as: the rep's pick, else what pricing resolved. */
  function chosen(line, i) {
    if (st.pick[i]) return st.pick[i];
    return line.status === 'matched' ? line.sku : null;
  }

  /** What this line could be quoted as. 🔴 Only parts we actually sell: an
   *  alternative that is not in our item master is shown beneath the row as
   *  information, never offered — pricing cannot price what we do not carry. */
  function lineOptions(line, i) {
    const opts = [];
    ((st.found || {})[i] || []).forEach(f => opts.push({ sku: f.code, why: 'you searched for it' }));
    if (line.status === 'matched' && line.sku) {
      opts.push({ sku: line.sku, why: line.settled_by || VIA[line.resolved_via] || '' });
    }
    (line.alternatives || []).forEach(a => opts.push({ sku: a, why: 'the same part, spelled another way in QX' }));
    (line.candidates || []).forEach(c => opts.push({ sku: c.sku, why: c.reason || '' }));
    ((line.xrefs || {}).ours || []).forEach(x => opts.push({
      sku: x.part, why: `${x.customer || 'a customer'} calls it this` }));
    ((line.xrefs || {}).alternatives || []).filter(x => x.in_catalogue).forEach(x => opts.push({
      sku: x.part, why: `${x.relationship}${x.supplier ? ` of ${x.supplier}` : ''}` }));
    return opts;
  }

  /** Alternatives we do NOT carry — worth knowing, not worth quoting. */
  function lineNotes(line) {
    const out = ((line.xrefs || {}).alternatives || []).filter(x => !x.in_catalogue)
      .map(x => `${x.part} (${x.relationship}${x.supplier ? `, ${x.supplier}` : ''}) — we don’t carry it`);
    return out.length ? `<div class="sc-list-meta">${out.map(esc).join('<br>')}</div>` : '';
  }

  /** A rep's own search, for a line pricing could not settle. Results are OUR parts
   *  only (the Find search); choosing one quotes the line as it. */
  function runSearch(i, q) {
    const out = content.querySelector(`[data-search-out="${i}"]`);
    if (!out) return;
    q = (q || '').trim();
    if (q.length < 2) { out.textContent = 'type at least two characters'; return; }
    out.textContent = 'searching…';
    salesApi.findParts(q, { entity: account.entity }).then(r => {
      const items = ((r.data || {}).items || []).slice(0, 12);
      if (!items.length) { out.textContent = 'none of our parts match that'; return; }
      out.innerHTML = items.map((it, k) => `<button type="button" class="btn btn-ghost btn-sm" data-found="${k}"
          style="display:block;text-align:left;padding:2px 4px;white-space:normal">
          <strong>${esc(it.code)}</strong>${it.subtitle ? ` · ${esc(it.subtitle)}` : ''}</button>`).join('');
      out.querySelectorAll('[data-found]').forEach(b => b.addEventListener('click', () => {
        const it = items[Number(b.dataset.found)];
        st.found = st.found || {};
        st.found[i] = [it, ...((st.found[i] || []).filter(f => f.code !== it.code))];
        st.pick[i] = it.code;
        stepRead();
      }));
    }).catch(e => { out.textContent = `could not search: ${e.message || 'unknown error'}`; });
  }

  function stepRead() {
    const p = st.parse;
    const lines = p.lines || [];
    const doc = p.document || {};
    const buyer = ((p.customer_match || {}).best) || null;
    const elsewhere = buyer && buyer.customer_code && buyer.customer_code !== account.code;
    const rows = lines.map((line, i) => {
      const [label, colour] = STATUS[line.status] || [line.status, 'gray'];
      const opts = lineOptions(line, i);
      const pickedSku = chosen(line, i);
      const attention = needsYou(line, i);
      if (st.onlyAttention && !attention) return '';
      return `<tr data-row="${i}" style="${st.drop[i] ? 'opacity:.45;' : ''}${attention
        ? 'background:var(--amber-50,#fffbeb);box-shadow:inset 3px 0 0 var(--amber-500,#f59e0b);' : ''}">
        <td style="width:32px"><input type="checkbox" data-keep="${i}"${st.drop[i] ? '' : ' checked'}
          aria-label="Include ${esc(line.written || line.sku || 'this line')}"></td>
        <td><strong>${esc(line.written || line.sku || '—')}</strong>
          ${line.customer_part ? `<div class="sc-list-meta">their number ${esc(line.customer_part)}</div>` : ''}
          ${(line.also_requested_at || []).length ? `<div class="sc-list-meta">
            ${getIcon('tag', 11)} a price break — they also asked at
            ${esc((line.also_requested_at || []).map(q => Number(q).toLocaleString()).join(' and '))}</div>` : ''}
          ${line.segment ? `<div class="sc-list-meta">${esc(line.segment)}</div>` : ''}
          ${lineNotes(line)}</td>
        <td><span class="badge badge-${colour}">${esc(label)}</span>
          ${line.confidence === 'low' ? '<div class="sc-list-meta">worth a look</div>' : ''}
          ${(line.flags || []).filter(f => !SAID_ELSEWHERE.includes(f))
        .map(f => `<div class="sc-list-meta">${esc(f.replace(/_/g, ' '))}</div>`).join('')}</td>
        <td>${opts.length ? `<select class="form-select" data-pick="${i}" style="min-width:210px;padding:4px 6px">
            ${pickedSku ? '' : '<option value="">— choose —</option>'}
            ${opts.map(o => `<option value="${esc(o.sku)}"${o.sku === pickedSku ? ' selected' : ''}>${esc(o.sku)}${o.why ? ` · ${esc(o.why)}` : ''}</option>`).join('')}
          </select>` : '<span class="fig-unknown">nothing of ours matches</span>'}
          <div><button type="button" class="btn btn-ghost btn-sm" data-search="${i}"
            style="padding:2px 4px">${getIcon('search', 12)} ${opts.length ? 'search for another part' : 'search our parts'}</button></div>
          <div data-search-box="${i}" hidden style="margin-top:4px">
            <input class="form-input" data-search-q="${i}" placeholder="part number, or part of one"
              value="${esc(line.written || line.sku || '')}" style="min-width:210px;padding:4px 6px">
            <div data-search-out="${i}" class="sc-list-meta" style="margin-top:4px"></div></div></td>
        <td style="text-align:right">${qtyCell(line, i)}</td>
      </tr>`;
    }).join('');

    content.innerHTML = `
      ${doc.kind === 'po' ? `<div class="card card-body" style="border-left:3px solid var(--amber-500,#f59e0b);margin-bottom:var(--space-3)">
        ${getIcon('alert-triangle', 14)} <strong>This is a purchase order${doc.reference ? ` (${esc(doc.reference)})` : ''}, not an enquiry.</strong>
        <div class="sc-list-meta">Quoting it back is rarely what they want — it usually needs keying in as an order.
          You can still price the lines here to check them.</div></div>` : ''}
      ${elsewhere ? `<div class="card card-body" style="border-left:3px solid var(--amber-500,#f59e0b);margin-bottom:var(--space-3)">
        ${getIcon('alert-triangle', 14)} The document looks like it is from <strong>${esc(buyer.name || buyer.customer_code)}</strong>,
        and you are quoting ${esc(account.name || account.code)}.
        <div class="sc-list-meta">Nothing has been switched — close this and open the right account if it is wrong.</div></div>` : ''}
      ${(p.warnings || []).length ? `<p class="sc-sheet-hint">${getIcon('alert-triangle', 12)} ${esc(p.warnings.join(' · '))}</p>` : ''}
      ${(() => {
        const n = lines.filter((l, i) => needsYou(l, i)).length;
        return n ? `<div class="card card-body" style="border-left:3px solid var(--amber-500,#f59e0b);margin-bottom:var(--space-3);display:flex;gap:var(--space-3);align-items:center;flex-wrap:wrap">
          <div style="flex:1">${getIcon('alert-triangle', 14)} <strong>${n} line${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} you</strong>
            <div class="sc-list-meta">Highlighted below — pick the part, search for it, or set the quantity. Everything else is ready.</div></div>
          <label class="sc-list-meta" style="display:flex;gap:6px;align-items:center;cursor:pointer">
            <input type="checkbox" data-only-attention${st.onlyAttention ? ' checked' : ''}> show only these</label></div>`
          : `<p class="sc-sheet-hint">${getIcon('check-circle', 12)} Every line is matched — check the quantities and price it.</p>`;
      })()}
      <div class="sc-list-meta" style="margin-bottom:var(--space-2)">
        Read ${lines.length} line${lines.length === 1 ? '' : 's'} — ${p.matched_count || 0} ours,
        ${p.review_count || 0} to check, ${p.unrecognised_count || 0} not ours.
        ${p.seconds ? ` Took ${Math.round(p.seconds)}s.` : ''}</div>
      <div class="card" style="padding:0;overflow:hidden"><div style="overflow-x:auto">
        <table class="data-table"><thead><tr><th></th><th>What they wrote</th><th></th>
          <th>Quote as</th><th style="text-align:right">Qty</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="5">Nothing in that document looked like a part number.</td></tr>'}</tbody>
      </table></div></div>
      ${(p.ignored || []).length ? `<details style="margin-top:var(--space-3)">
        <summary class="sc-list-meta">${p.ignored.length} thing${p.ignored.length === 1 ? '' : 's'} read and left out</summary>
        <div class="sc-list-meta" style="margin-top:var(--space-2)">${p.ignored.map((g, gi) =>
        `${esc(g.text)} — ${esc(g.reason || '')}
         <button type="button" class="btn btn-ghost btn-sm" data-restore="${gi}" style="padding:0 4px">${getIcon('plus', 11)} add it back</button>`).join('<br>')}</div></details>` : ''}
      <p class="sc-sheet-hint" style="margin-top:var(--space-3)">Untick anything they are not asking for.
        The same part twice is a price break, not a mistake — leave both.</p>
      <p class="sc-sheet-hint">${getIcon('alert-triangle', 12)} <strong>Count the lines against
        ${p.filename ? esc(p.filename) : 'what they sent'}.</strong> Everything read is above —
        a line that was missed cannot appear here, and that is the one thing this screen
        cannot show you.</p>
      <p class="sc-sheet-hint" style="color:var(--amber-600,#d97706)" data-qty-waiting></p>`;

    content.querySelectorAll('[data-keep]').forEach(box => box.addEventListener('change', () => {
      st.drop[Number(box.dataset.keep)] = !box.checked;
      box.closest('tr').style.opacity = box.checked ? '' : '.45';
      count();
    }));
    content.querySelectorAll('[data-pick]').forEach(sel => sel.addEventListener('change', () => {
      st.pick[Number(sel.dataset.pick)] = sel.value || null;
      stepRead();
    }));
    const only = content.querySelector('[data-only-attention]');
    if (only) only.addEventListener('change', () => { st.onlyAttention = only.checked; stepRead(); });
    content.querySelectorAll('[data-restore]').forEach(b => b.addEventListener('click', () => {
      const g = p.ignored.splice(Number(b.dataset.restore), 1)[0];
      // Back in as a line to resolve by hand: whatever pricing suggested comes with it.
      lines.push({ sku: g.text, written: g.text, qty: g.qty, status: 'unrecognised', flags: ['restored'],
        candidates: g.candidates || [], source: g.source, confidence: 'low' });
      stepRead();
    }));
    content.querySelectorAll('[data-search]').forEach(b => b.addEventListener('click', () => {
      const i = Number(b.dataset.search);
      const box = content.querySelector(`[data-search-box="${i}"]`);
      box.hidden = !box.hidden;
      if (!box.hidden) { const inp = box.querySelector('input'); inp.focus(); inp.select(); runSearch(i, inp.value); }
    }));
    content.querySelectorAll('[data-search-q]').forEach(inp => {
      let t;
      inp.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => runSearch(Number(inp.dataset.searchQ), inp.value), 300); });
    });
    content.querySelectorAll('[data-qty]').forEach(inp => inp.addEventListener('input', () => {
      const i = Number(inp.dataset.qty);
      st.qty[i] = inp.value === '' ? null : Number(inp.value);
      // typing IS the check: the amber comes off this box and the line becomes priceable
      inp.style.borderColor = ''; inp.style.background = '';
      const cell = inp.closest('td');
      cell.querySelectorAll('.sc-list-meta').forEach(n => n.remove());
      count();
    }));
    content.querySelectorAll('[data-qty-alt]').forEach(btn => btn.addEventListener('click', () => {
      const i = Number(btn.dataset.qtyAlt);
      const box = content.querySelector(`[data-qty="${i}"]`);
      box.value = btn.dataset.qtyValue;
      box.dispatchEvent(new Event('input'));
    }));

    function ready() {
      return (st.parse.lines || []).map((line, i) => {
        if (st.drop[i]) return null;
        const sku = chosen(line, i);
        const qty = st.qty[i] != null ? st.qty[i] : line.qty;
        // 🔴 an unconfirmed `qty_missing` / `qty_conflict` line is NOT ready — pricing
        // 500 of something as 1 is a wrong price that reads like a right one
        const stillAssumed = st.qty[i] == null && needsQty(line);
        return sku && qty > 0 && !stillAssumed ? { sku, qty } : null;
      }).filter(Boolean);
    }

    function unchecked() {
      return (st.parse.lines || []).filter((line, i) => !st.drop[i] && st.qty[i] == null
        && needsQty(line)).length;
    }

    function count() {
      const n = ready().length;
      const waiting = unchecked();
      const btn = footer.querySelector('[data-price-read]');
      if (btn) {
        btn.textContent = n ? `Price ${n} line${n === 1 ? '' : 's'}` : 'Nothing to price yet';
        btn.disabled = !n;
      }
      const note = content.querySelector('[data-qty-waiting]');
      if (note) {
        note.textContent = waiting
          ? `${waiting} line${waiting === 1 ? '' : 's'} need a quantity before ${waiting === 1 ? 'it' : 'they'} can be priced.`
          : '';
      }
    }

    setActions([
      { label: 'Back', cls: 'btn btn-secondary', fn: () => stepLines(st.text) },
      { label: 'Price', cls: 'btn btn-primary', fn: () => price(ready()) },
    ]);
    footer.lastChild.setAttribute('data-price-read', '');
    count();
  }

  async function price(given) {
    if (st.busy) return;
    let lines = given;
    if (!lines) {
      const ta = content.querySelector('#qs-lines');
      const parsed = parseLines(ta.value);
      if (!parsed.lines.length) { showError('Add at least one part number.'); return; }
      if (parsed.bad.length) { showError(`Line ${parsed.bad.join(', ')} has no usable quantity.`); return; }
      lines = parsed.lines;
      st.text = ta.value;
    }
    if (!lines.length) { showError('Tick at least one line, and give it a part and a quantity.'); return; }
    const surgeBox = content.querySelector('#qs-surge');
    if (surgeBox) st.surge = surgeBox.checked;
    // the button says "Price 7 lines" on the review step and "Price it" on the textarea
    const label = (footer.lastChild && footer.lastChild.textContent) || 'Price it';
    st.busy = true;
    saving(modal, true, label);
    try {
      const res = await salesApi.buildQuote({
        entity: account.entity, customer_code: account.code, lines, apply_surge: st.surge,
        strategy: st.strategy || undefined,
        previous_quote: st.winback ? st.winback.previous_quote : undefined });
      const q = (res.data || {}).quote;
      if (!q) {
        const src = ((res.meta || {}).sources || {}).pricing || {};
        showError(src.reason || 'The pricing engine did not answer.');
        saving(modal, false, label);
        return;
      }
      st.built = q; st.edits = {}; st.delivery = {};
      stepPriced();
    } catch (err) {
      showError(err.message || 'Could not price the lines.');
      saving(modal, false, label);
    } finally {
      st.busy = false;
    }
  }

  // ── 2. the priced lines ───────────────────────────────────────────────────
  function effective(l, i) { return st.edits[i] != null ? st.edits[i] : l.recommended_price; }

  function stepPriced() {
    const q = st.built;
    const ccy = q.currency || 'GBP';
    const m = money(ccy, 2);
    const hasCost = q.lines.some(l => 'cost' in l);
    const unpriced = q.lines.filter(l => l.recommended_price == null).length;
    const marginOf = (l, eff) => (hasCost && l.cost && eff ? ((eff - l.cost) / eff) * 100 : null);
    const rows = q.lines.map((l, i) => {
      const eff = effective(l, i);
      const onBook = (l.rationale || {}).basis === 'price_book';
      return `<tr${onBook ? ' style="box-shadow:inset 4px 0 0 var(--green-600,#059669)"' : ''}>
        <td style="width:28px"><button type="button" class="btn btn-ghost btn-sm" data-work="${i}" aria-expanded="false"
            title="How this price was worked out" style="padding:2px 6px">▸</button></td>
        <td><strong>${esc(l.sku)}</strong><div class="sc-list-meta">${esc(l.description || '')}</div></td>
        <td style="text-align:right">${esc(String(l.qty))}</td>
        <td style="text-align:right;font-variant-numeric:tabular-nums">${l.recommended_price == null ? '<span class="fig-unknown" title="No price data">—</span>' : esc(onBook ? money(ccy, 4)(l.recommended_price) : m(l.recommended_price))}${bookPill(l)}</td>
        <td style="text-align:right"><input class="form-input" data-price="${i}" type="number" min="0" step="0.0001"
            value="${eff == null ? '' : eff}" style="width:100px;text-align:right;padding:4px 6px" aria-label="Your price for ${esc(l.sku)}">
            <div data-book-warn="${i}">${bookOverrideNote(l, st.edits[i], ccy)}</div></td>
        ${hasCost ? `<td style="text-align:right" data-margin="${i}">${marginCell(marginOf(l, eff), st.edits[i] != null ? l.margin_pct : null)}</td>` : ''}
        <td>${confidenceBadge(l.confidence)}</td>
        <td><input class="form-input" data-delivery="${i}" maxlength="80" placeholder="e.g. 4 weeks" value="${esc(st.delivery[i] || '')}"
            style="width:110px;padding:4px 6px" aria-label="Delivery for ${esc(l.sku)}"></td>
        <td style="font-size:var(--text-xs);max-width:260px">${why(l, ccy)}</td>
      </tr>
      <tr data-work-row="${i}" hidden><td></td><td colspan="${hasCost ? 8 : 7}" style="background:var(--surface-secondary,#f9fafb)">${workingsHtml(l, ccy)}</td></tr>`;
    }).join('');
    content.innerHTML = `
      ${quoteContext(q)}
      <div style="display:flex;justify-content:space-between;gap:var(--space-3);flex-wrap:wrap;margin-bottom:var(--space-3)">
        <div class="sc-list-meta">Priced on <strong>${esc((q.brand || {}).name || q.company_id)}</strong>’s catalogue in ${esc(ccy)}
          ${unpriced ? ` · <span style="color:var(--amber-600,#d97706)">${unpriced} line${unpriced === 1 ? ' has' : 's have'} no price data — set a price or it goes out blank</span>` : ''}</div>
        <div style="font-weight:600" data-total></div>
      </div>
      <div class="card" style="padding:0;overflow:hidden"><div style="overflow-x:auto">
        <table class="data-table">
          <thead><tr><th></th><th>Part</th><th style="text-align:right">Qty</th><th style="text-align:right">Suggested</th>
            <th style="text-align:right">Your price</th>${hasCost ? '<th style="text-align:right">Margin</th>' : ''}<th>Confidence</th>
            <th>Delivery</th><th>Why</th></tr></thead>
          <tbody>${rows}</tbody>
        </table></div></div>
      <p class="sc-sheet-hint" style="margin-top:var(--space-3)">▸ on a line shows everything pricing weighed — what they paid,
        what we quoted, similar customers, competitors with stock and lead time, our cost — and how the price was reached.
        Saving puts a draft in the pricing desk’s quote list. Valid for 30 days. Nothing is sent to the customer.</p>`;
    content.querySelectorAll('[data-work]').forEach(btn => btn.addEventListener('click', () => {
      const i = Number(btn.dataset.work);
      const row = content.querySelector(`[data-work-row="${i}"]`);
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      btn.textContent = open ? '▸' : '▾';
      row.hidden = open;
      if (!open) loadCompetitors(row.querySelector('[data-competitors]'), account.entity, q.lines[i].sku, account.code);
    }));
    const total = () => {
      const t = q.lines.reduce((a, l, i) => a + (effective(l, i) || 0) * l.qty, 0);
      content.querySelector('[data-total]').textContent = `Total ${m(t)}`;
    };
    content.querySelectorAll('[data-price]').forEach(inp => inp.addEventListener('input', () => {
      const i = Number(inp.dataset.price);
      st.edits[i] = inp.value === '' ? null : Number(inp.value);
      const cell = content.querySelector(`[data-margin="${i}"]`);
      if (cell) {
        const l = q.lines[i];
        cell.innerHTML = marginCell(marginOf(l, effective(l, i)), st.edits[i] != null ? l.margin_pct : null);
      }
      const warn = content.querySelector(`[data-book-warn="${i}"]`);
      if (warn) warn.innerHTML = bookOverrideNote(q.lines[i], st.edits[i], ccy);
      total();
    }));
    content.querySelectorAll('[data-delivery]').forEach(inp => inp.addEventListener('input', () => {
      st.delivery[Number(inp.dataset.delivery)] = inp.value.trim();
    }));
    total();
    setActions([
      { label: st.parse ? 'Back to what they sent' : 'Back to the lines', cls: 'btn btn-secondary',
        fn: () => (st.parse ? stepRead() : stepLines(st.text)) },
      ...(writer ? [{ label: 'Save draft', cls: 'btn btn-primary', fn: () => save() }] : []),
    ]);
  }

  async function save() {
    if (st.busy) return;
    const q = st.built;
    const missing = q.lines.filter((l, i) => effective(l, i) == null).map(l => l.sku);
    if (missing.length && !window.confirm(`${missing.join(', ')} ${missing.length === 1 ? 'has' : 'have'} no price. Save anyway?`)) return;
    st.busy = true;
    saving(modal, true, 'Save draft');
    try {
      const res = await salesApi.saveQuote({
        entity: account.entity, customer_code: account.code, apply_surge: st.surge,
        strategy: st.strategy || undefined,
        previous_quote: st.winback ? st.winback.previous_quote : undefined,
        work_item_id: opts.workItem ? opts.workItem.id : undefined,
        // built FROM a logged quote request: the save stamps it quoted (response times)
        request_id: opts.requestId || undefined,
        lines: q.lines.map((l, i) => ({
          sku: l.sku, qty: l.qty,
          final_price: st.edits[i] != null ? st.edits[i] : undefined,
          delivery: st.delivery[i] || undefined,
        })),
      });
      st.saved = res.data;
      window.dispatchEvent(new CustomEvent('sales:quotes-changed', { detail: { entity: account.entity, code: account.code } }));
      if (res.data.linked) window.dispatchEvent(new CustomEvent('sales:items-changed', { detail: { id: res.data.linked.work_item_id } }));
      showSuccess(`Saved as ${quoteNumber(res.data)}.`);
      stepSaved();
    } catch (err) {
      showError(err.message || 'The quote was not saved.');
      saving(modal, false, 'Save draft');
    } finally {
      st.busy = false;
    }
  }

  // ── 3. saved ──────────────────────────────────────────────────────────────
  function stepSaved() {
    const s = st.saved;
    const link = s.linked;
    content.innerHTML = `
      <div class="card card-body" style="text-align:center;padding:var(--space-6)">
        <div style="font-size:var(--text-lg);font-weight:600">${getIcon('check-circle', 18)} ${esc(quoteNumber(s))} saved as a draft</div>
        <p class="sc-list-meta">${s.line_count} line${s.line_count === 1 ? '' : 's'} · ${esc(money(s.currency || 'GBP', 2)(s.total_value || 0))}
          · in the pricing desk’s quote list</p>
        ${link ? `<p class="sc-list-meta">${link.ok ? `${getIcon('target', 12)} Linked to ${esc(opts.workItem.title)}.`
          : `${getIcon('alert-triangle', 12)} Saved, but not linked: ${esc(link.reason || '')}`}</p>` : ''}
        <p style="margin-top:var(--space-4);display:flex;gap:var(--space-2);justify-content:center;flex-wrap:wrap">
          <a class="btn btn-secondary" href="${salesApi.quotePdfUrl(s.id)}" target="_blank" rel="noopener">
            ${getIcon('download', 14)} Open the PDF</a>
          <a class="btn btn-secondary" href="${salesApi.quoteQxUrl(s.id, 'quote')}"
             title="A CSV for QX's quote import — pick the same customer in QX when you import it">${getIcon('file-text', 14)} QX quote file</a>
          <button type="button" class="btn btn-secondary" data-qx-order
             title="A CSV for QX's sales order import — needs the customer's PO number">${getIcon('shopping-cart', 14)} QX order file</button>
          ${writer ? `<button class="btn btn-primary" data-send>${getIcon('mail', 14)} Send it to the customer</button>` : ''}</p>
      </div>`;
    // the QX import files were only in the workbench, so a quote just built here had no way to them
    content.querySelector('[data-qx-order]')?.addEventListener('click', () => {
      const po = (window.prompt("Customer's PO number for this order:") || '').trim();
      if (po) window.location.href = salesApi.quoteQxUrl(s.id, 'order', po);
    });
    const send = content.querySelector('[data-send]');
    if (send) {
      send.addEventListener('click', async () => {
        const { openQuoteSend } = await import('./quote-send-sheet.js');
        modal.close();
        openQuoteSend({ ...s, entity_code: account.entity, customer_code: account.code },
                      { workItem: opts.workItem, accountName: account.name || account.code });
      });
    }
    setActions([{ label: 'Done', cls: 'btn btn-secondary', fn: () => modal.close() }]);
  }

  stepLines();
  return modal;
}

export function quoteNumber(q) {
  return q.quote_number || `Q${String(q.id).padStart(5, '0')}`;
}
